import * as React from "react";
import { Box, Stack } from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { Certification } from "../../types/certification";
import LoadingButton from "@mui/lab/LoadingButton";
import {
  useGetCertificationByIdQuery,
  useAddCertificationMutation,
  useEditCertificationMutation,
} from "../../services/certification.service";
import { BaseModal, StyledTextField, Button } from "../../components/global";

interface ModalCertificationFormProps {
  open: boolean;
  onClose: () => void;
  data: Certification | null;
}

const ModalCertificationForm = ({
  open,
  onClose,
  data,
}: ModalCertificationFormProps) => {
  const { data: cert } = useGetCertificationByIdQuery(data?.id!, {
    skip: !data?.id,
  });

  const [add, { isLoading: adding }] = useAddCertificationMutation();
  const [edit, { isLoading: editing }] = useEditCertificationMutation();

  const {
    handleSubmit,
    clearErrors,
    control,
    setValue,
    formState: { errors },
  } = useForm();

  const onSubmit = async (payload: any) => {
    try {
      if (!data) await add(payload).unwrap();
      else {
        payload.id = data?.id!;
        await edit(payload).unwrap();
      }
      onClose();
    } catch (err) {
      onClose();
    }
  };

  React.useEffect(() => {
    if (open) {
      setValue("name", data?.name || "");
      setValue("provider", data?.provider || "");
      setValue("description", data?.description || "");
      clearErrors();
    }
  }, [data, open, setValue, clearErrors]);

  React.useEffect(() => {
    if (cert) {
      setValue("name", cert?.name || "");
      setValue("provider", cert?.provider || "");
      setValue("description", cert?.description || "");
    }
  }, [cert, setValue]);

  return (
    <BaseModal
      open={open}
      onClose={onClose}
      title={`${!data ? "Create" : "Edit"} Certification`}
      maxWidth="xs"
      useGlassCard={false}
      footer={
        <>
          <Button variant="outlined" onClick={onClose}>
            Cancel
          </Button>
          <LoadingButton
            type="submit"
            variant="contained"
            loading={adding || editing}
            onClick={handleSubmit(onSubmit)}
          >
            {!data ? "Create" : "Update"}
          </LoadingButton>
        </>
      }
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <Box sx={{ p: 3 }}>
          <Stack spacing={2}>
            <Controller
              name="name"
              control={control}
              rules={{ required: true }}
              render={({ field: { onChange, onBlur, value, ref } }) => (
                <StyledTextField
                  label="Certification Name"
                  value={value || ""}
                  fullWidth
                  onChange={onChange}
                  onBlur={onBlur}
                  inputRef={ref}
                  error={!!errors.name}
                  helperText={!!errors.name && "This field is required"}
                />
              )}
            />
            <Controller
              name="provider"
              control={control}
              render={({ field: { onChange, onBlur, value, ref } }) => (
                <StyledTextField
                  label="Provider Name"
                  value={value || ""}
                  fullWidth
                  onChange={onChange}
                  onBlur={onBlur}
                  inputRef={ref}
                  placeholder="Enter provider name (optional)"
                />
              )}
            />
            <Controller
              name="description"
              control={control}
              render={({ field: { onChange, onBlur, value, ref } }) => (
                <StyledTextField
                  label="Description"
                  value={value || ""}
                  fullWidth
                  onChange={onChange}
                  onBlur={onBlur}
                  inputRef={ref}
                  multiline
                  rows={3}
                  placeholder="Enter description (optional)"
                />
              )}
            />
          </Stack>
        </Box>
      </form>
    </BaseModal>
  );
};
export default ModalCertificationForm;
